import { useMemo, useState } from 'react';
import { Box, Button, Menu, MenuItem, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import type { ExcelImportCategory, ExcelImportIssue } from '../../services/creditImportService';
import type { ColumnMapping, ImportRowDraft } from '../../utils/creditImportValidation';

interface Props {
  mode: 'self' | 'tenant';
  row: ImportRowDraft;
  issues: ExcelImportIssue[];
  categories: ExcelImportCategory[];
  deliveryMethods: string[];
  columnMapping: ColumnMapping;
  /** Firm mode: the server is re-matching an edited email for this row. */
  emailPending?: boolean;
  onChange: (row: ImportRowDraft) => void;
}

type CoreField = 'email' | 'activityTitle' | 'dateEarned' | 'providerName' | 'nasbaSponsorId' | 'deliveryMethod' | 'notes';
const ALL_CORE: CoreField[] = ['email', 'activityTitle', 'dateEarned', 'providerName', 'nasbaSponsorId', 'deliveryMethod', 'notes'];
/** Self-service sheets have no Email column; stable references so memoized derivations can depend on them. */
const SELF_CORE: CoreField[] = ALL_CORE.filter((f) => f !== 'email');
const LABELS: Record<CoreField, string> = {
  email: 'Email',
  activityTitle: 'Activity title',
  dateEarned: 'Date earned',
  providerName: 'Provider',
  nasbaSponsorId: 'NASBA sponsor ID',
  deliveryMethod: 'Delivery method',
  notes: 'Notes',
};

/**
 * Inline editor under a review row. Shows the row's messages as text, then only the fields that need
 * fixing (flagged fields), with "Edit all fields" revealing the rest. Inputs commit on blur or Enter.
 */
export default function ImportRowEditor({ mode, row, issues, categories, deliveryMethods, columnMapping, emailPending, onChange }: Props) {
  // Email is a column only in firm mode, where each row must match a person.
  const CORE_ORDER = mode === 'tenant' ? ALL_CORE : SELF_CORE;
  const [showAll, setShowAll] = useState(issues.length === 0);
  const [addMenuAnchor, setAddMenuAnchor] = useState<HTMLElement | null>(null);
  // Once the user touches hours, keep the hours inputs and the add list on screen even after the row is fixed,
  // so the editor does not collapse under them mid-edit.
  const [hoursTouched, setHoursTouched] = useState(false);

  const issueFor = (field: string) => issues.find((i) => i.field === field)?.message;
  // Messages are shown once, in the list above the fields; inputs point at their list item for screen readers.
  const describedBy = (field: string) => {
    const idx = issues.findIndex((i) => i.field === field);
    return idx >= 0 ? `row-${row.rowNumber}-issue-${idx}` : undefined;
  };
  const flagged = useMemo(() => {
    const fields = issues.map((i) => i.field);
    return {
      core: new Set(fields.filter((f): f is CoreField => (CORE_ORDER as string[]).includes(f))),
      hours: new Set(fields.filter((f) => f.startsWith('hours:')).map((f) => f.slice('hours:'.length))),
      unmapped: new Set(fields.filter((f) => f.startsWith('unmapped:')).map((f) => f.slice('unmapped:'.length))),
      hoursProblem: fields.includes('hours'),
    };
  }, [issues, CORE_ORDER]);
  const hoursProblem = flagged.hoursProblem || hoursTouched;

  const coreFields = showAll ? CORE_ORDER : CORE_ORDER.filter((f) => flagged.core.has(f));
  const hourCategoryIds = useMemo(() => {
    const present = Object.keys(row.hours);
    if (showAll || flagged.hoursProblem || hoursTouched) return present;
    return present.filter((id) => flagged.hours.has(id));
  }, [row.hours, showAll, flagged, hoursTouched]);
  const unmappedHeaders = useMemo(() => {
    const present = Object.keys(row.unmappedHours).filter((h) => columnMapping[h] !== 'ignore');
    if (showAll) return present;
    return present.filter((h) => flagged.unmapped.has(h));
  }, [row.unmappedHours, showAll, flagged, columnMapping]);

  const hiddenCount =
    CORE_ORDER.length - coreFields.length
    + Object.keys(row.hours).length - hourCategoryIds.length;

  const commitCore = (field: CoreField, raw: string) => {
    const value = raw.trim().length === 0 ? null : raw;
    if ((row[field] ?? null) === value) return;
    onChange({ ...row, [field]: value, ...(field === 'dateEarned' ? { dateEarnedRaw: value ? null : row.dateEarnedRaw } : {}) });
  };

  // Blank or zero removes the field of study from the row, so it comes back in the "Add" list.
  const commitHours = (categoryId: string, raw: string) => {
    setHoursTouched(true);
    const hours = { ...row.hours };
    const n = Number(raw);
    if (raw.trim() === '' || n === 0) delete hours[categoryId];
    else {
      if (Number.isNaN(n)) return;
      hours[categoryId] = n;
    }
    onChange({ ...row, hours });
  };

  const commitUnmapped = (header: string, raw: string) => {
    setHoursTouched(true);
    const unmappedHours = { ...row.unmappedHours };
    const n = Number(raw);
    if (raw.trim() === '' || n === 0) delete unmappedHours[header];
    else {
      if (Number.isNaN(n)) return;
      unmappedHours[header] = n;
    }
    onChange({ ...row, unmappedHours });
  };

  const categoryName = (id: string) => categories.find((c) => String(c.id) === id)?.name ?? `Category ${id}`;
  // Categories that already have an hours input on this row are not offered again.
  const availableToAdd = categories.filter((c) => !(String(c.id) in row.hours));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, py: 1 }}>
      {issues.length > 0 && (
        <Box component="ul" sx={{ m: 0, pl: 2, color: 'status.attention.fg' }} aria-label="What needs attention">
          {issues.map((i, idx) => (
            <Typography key={idx} component="li" variant="body2" sx={{ fontWeight: 600 }} id={`row-${row.rowNumber}-issue-${idx}`}>
              {i.message}
            </Typography>
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start' }}>
        {coreFields.map((field) => {
          const message = issueFor(field);
          const common = {
            size: 'small' as const,
            label: LABELS[field],
            error: !!message,
            inputProps: { 'aria-invalid': !!message, 'aria-describedby': describedBy(field) },
          };
          if (field === 'deliveryMethod') {
            const value = deliveryMethods.includes(row.deliveryMethod ?? '') ? row.deliveryMethod ?? '' : '';
            return (
              <TextField
                key={field}
                {...common}
                select
                value={value}
                onChange={(e) => commitCore(field, e.target.value)}
                helperText={message && row.deliveryMethod && !value ? `Sheet had “${row.deliveryMethod}”.` : undefined}
                sx={{ minWidth: 220 }}
                SelectProps={{ displayEmpty: true, renderValue: (v) => (v === '' ? <em>Leave blank</em> : String(v)) }}
              >
                <MenuItem value="">
                  <em>Leave blank</em>
                </MenuItem>
                {deliveryMethods.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </TextField>
            );
          }
          if (field === 'dateEarned') {
            return (
              <TextField
                key={field}
                {...common}
                type="date"
                defaultValue={row.dateEarned ?? ''}
                helperText={message && row.dateEarnedRaw ? `Sheet had “${row.dateEarnedRaw}”.` : undefined}
                InputLabelProps={{ shrink: true }}
                onBlur={(e) => commitCore(field, e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                sx={{ minWidth: 180 }}
              />
            );
          }
          return (
            <TextField
              key={field}
              {...common}
              type={field === 'email' ? 'email' : undefined}
              helperText={field === 'email' && emailPending ? 'Checking…' : undefined}
              defaultValue={row[field] ?? ''}
              multiline={field === 'notes'}
              minRows={field === 'notes' ? 2 : undefined}
              onBlur={(e) => commitCore(field, e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && field !== 'notes') (e.target as HTMLInputElement).blur(); }}
              sx={{ minWidth: field === 'activityTitle' || field === 'notes' ? 320 : field === 'email' ? 260 : 200, flex: field === 'notes' ? '1 1 100%' : undefined }}
            />
          );
        })}

        {hourCategoryIds.map((id) => {
          const message = issueFor(`hours:${id}`);
          return (
            <TextField
              key={`h-${id}`}
              size="small"
              type="number"
              label={`${categoryName(id)} hours`}
              defaultValue={row.hours[id] ?? ''}
              error={!!message}
              inputProps={{ step: 0.1, min: 0, 'aria-invalid': !!message, 'aria-describedby': describedBy(`hours:${id}`) }}
              onBlur={(e) => commitHours(id, e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
              sx={{ width: 180 }}
            />
          );
        })}

        {unmappedHeaders.map((header) => {
          const message = issueFor(`unmapped:${header}`);
          const mapped = columnMapping[header];
          const label = typeof mapped === 'number' ? `${categoryName(String(mapped))} hours (${header})` : `${header} hours`;
          return (
            <TextField
              key={`u-${header}`}
              size="small"
              type="number"
              label={label}
              defaultValue={row.unmappedHours[header] ?? ''}
              error={!!message}
              helperText={mapped === undefined ? 'Decide this column above.' : undefined}
              inputProps={{ step: 0.1, min: 0, 'aria-invalid': !!message, 'aria-describedby': describedBy(`unmapped:${header}`) }}
              onBlur={(e) => commitUnmapped(header, e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
              sx={{ width: 220 }}
            />
          );
        })}

        {/* An action, not a field: adds an hours input for another category to this row */}
        {(showAll || hoursProblem) && availableToAdd.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              aria-haspopup="menu"
              aria-expanded={addMenuAnchor ? 'true' : undefined}
              onClick={(e) => setAddMenuAnchor(e.currentTarget)}
            >
              Add field of study
            </Button>
            <Menu
              anchorEl={addMenuAnchor}
              open={addMenuAnchor !== null}
              onClose={() => setAddMenuAnchor(null)}
              slotProps={{ paper: { sx: { maxHeight: 360 } } }}
            >
              {availableToAdd.map((c) => (
                <MenuItem
                  key={c.id}
                  onClick={() => {
                    setAddMenuAnchor(null);
                    setHoursTouched(true);
                    onChange({ ...row, hours: { ...row.hours, [String(c.id)]: 0 } });
                  }}
                >
                  {c.name}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        )}
      </Box>

      {!showAll && hiddenCount > 0 && (
        <Button variant="text" size="small" onClick={() => setShowAll(true)} sx={{ alignSelf: 'flex-start' }}>
          Edit all fields
        </Button>
      )}
    </Box>
  );
}
